import React, { useEffect } from 'react';
import { useLanguage } from '@contexts/LanguageContext';
import { useCommonForm } from './hooks/useCommonForm';
import ErrorDisplay from './components/ErrorDisplay';

const LocationForm = ({ data, location, onSubmit, onCancel, isEditing = false, loading: externalLoading = false }) => {
  const { t } = useLanguage();
  // Support both `data` (from common components) and `location` (legacy)
  const modelData = data || location;

  const initialFormData = {
    name: modelData?.name || '',
    description: modelData?.description || '',
    notes: modelData?.notes || ''
  };

  const validationRules = {
    name: {
      required: true,
      requiredMessage: t('locations.locationForm.nameRequired') || 'Location name is required',
      minLength: 1,
      minLengthMessage: t('locations.locationForm.nameMinLength') || 'Location name cannot be empty'
    }
  };

  const {
    formData,
    loading,
    error,
    fieldErrors,
    handleSubmit,
    updateFormData,
    getFieldProps
  } = useCommonForm({
    initialData: initialFormData,
    onSubmit,
    externalLoading,
    validationRules
  });

  // Update form data when modelData changes
  useEffect(() => {
    if (modelData) {
      const newFormData = {
        name: modelData.name || '',
        description: modelData.description || '',
        notes: modelData.notes || ''
      };
      updateFormData(newFormData);
    } else {
      // Reset form for new location
      updateFormData({
        name: '',
        description: '',
        notes: ''
      });
    }
  }, [modelData]);

  return (
    <div className="sloot-form">
      <ErrorDisplay error={error} fieldErrors={fieldErrors} />

      <form onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="name">
              {t('locations.locationForm.name') || 'Name'} *
            </label>
            <input
              type="text"
              id="name"
              {...getFieldProps('name', {
                placeholder: t('locations.locationForm.namePlaceholder') || 'Enter location name',
                required: true,
                autoFocus: true
              })}
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="description">
            {t('locations.locationForm.description') || 'Description'}
          </label>
          <textarea
            id="description"
            {...getFieldProps('description', {
              placeholder: t('locations.locationForm.descriptionPlaceholder') || 'Enter location description',
              rows: 3
            })}
          />
        </div>

        <div className="form-group">
          <label htmlFor="notes">
            {t('locations.locationForm.notes') || 'Notes'}
          </label>
          <textarea
            id="notes"
            {...getFieldProps('notes', {
              placeholder: t('locations.locationForm.notesPlaceholder') || 'Additional notes about this location',
              rows: 3
            })}
          />
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={onCancel}
            className="btn btn-secondary"
            disabled={loading}
          >
            {t('common.cancel') || 'Cancel'}
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? (t('common.saving') || 'Saving...') : (isEditing ? (t('common.update') + ' ' + (t('locations.locationForm.name') || 'Location')) : (t('common.create') + ' ' + (t('locations.locationForm.name') || 'Location')))}
          </button>
        </div>
      </form>
    </div>
  );
};

export default LocationForm;
